import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr, imgUrl } from "@/lib/api";
import { AdminTitle } from "@/pages/admin/AdminLayout";
import { ImageUploader } from "./ImageUploader";

const EMPTY = { event_type: "", title: "", intro: "", cover: "", order: 0, visible: true };

export const CategoriesAdmin = () => {
  const [items, setItems] = useState([]);
  const [available, setAvailable] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/admin/categories").then((r) => { setItems(r.data.items); setAvailable(r.data.available); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { event_type: form.event_type, title: form.title, intro: form.intro, cover: form.cover, order: Number(form.order) || 0, visible: !!form.visible };
    try {
      if (form.id) await api.put(`/admin/categories/${form.id}`, body); else await api.post("/admin/categories", body);
      toast.success(form.id ? "Categoria aggiornata" : "Categoria creata con pagina e voce di menu");
      setForm(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (it) => {
    const parts = ["la voce nella home page", it.page_slug ? `la pagina /${it.page_slug}` : null, "la voce nel menu",
      it.stories ? `${it.stories} stori${it.stories === 1 ? "a" : "e"} del portfolio` : null].filter(Boolean);
    if (!window.confirm(`Eliminare "${it.title}"?\n\nVerranno rimossi:\n· ${parts.join("\n· ")}\n\nL'operazione non è reversibile.`)) return;
    try { await api.delete(`/admin/categories/${it.id}`); toast.success("Categoria eliminata"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const flip = async (it) => {
    try {
      await api.put(`/admin/categories/${it.id}`, { event_type: it.event_type, title: it.title, intro: it.intro, cover: it.cover, order: it.order, visible: !it.visible });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="admin-categories">
      <AdminTitle title="Categorie" subtitle="Le categorie mostrate in home page. Ognuna ha la sua pagina pubblica e la sua voce nel menu: creandola o eliminandola qui vengono create o rimosse anche quelle.">
        <button onClick={() => setForm({ ...EMPTY, event_type: available[0]?.value || "" })} disabled={!available.length}
          className="btn-ink !py-2.5 !px-5 disabled:opacity-40" data-testid="admin-categories-new-btn"><Plus size={14} /> Aggiungi</button>
      </AdminTitle>
      {!available.length && <p className="text-xs text-mute -mt-4 mb-6">Tutte le categorie disponibili sono già in uso.</p>}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((it, k) => (
          <div key={it.id} className="admin-card overflow-hidden flex flex-col" data-testid={`admin-categories-item-${k}`}>
            <div className="h-44 bg-cream">{it.cover && <img src={imgUrl(it.cover)} alt="" className="w-full h-full object-cover" />}</div>
            <div className="p-5 flex-1">
              <div className="flex justify-between gap-3">
                <p className="text-base font-semibold leading-tight">{it.title}</p>
                <span className={`text-[10px] h-fit px-2 py-1 rounded whitespace-nowrap ${it.visible ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{it.visible ? "Visibile" : "Nascosta"}</span>
              </div>
              <p className="text-xs text-mute mt-2">
                {it.page_slug ? <a href={`/${it.page_slug}`} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1" data-testid={`admin-categories-page-${k}`}>/{it.page_slug} <ExternalLink size={10} /></a> : "nessuna pagina collegata"}
                {` · ordine ${it.order} · ${it.stories} storie`}
              </p>
              {it.intro && <p className="text-sm text-stoneish mt-3 line-clamp-3">{it.intro}</p>}
            </div>
            <div className="flex border-t border-line text-xs">
              <button onClick={() => flip(it)} className="flex-1 py-3 flex items-center justify-center gap-1.5 hover:bg-cream" data-testid={`admin-categories-toggle-${k}`}>
                {it.visible ? <><EyeOff size={13} /> Nascondi</> : <><Eye size={13} /> Mostra</>}
              </button>
              <button onClick={() => setForm({ ...EMPTY, ...it })} className="flex-1 py-3 flex items-center justify-center gap-1.5 hover:bg-cream border-l border-line" data-testid={`admin-categories-edit-${k}`}><Pencil size={13} /> Modifica</button>
              <button onClick={() => remove(it)} className="py-3 px-4 text-terracotta hover:bg-cream border-l border-line" data-testid={`admin-categories-delete-${k}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-mute text-sm" data-testid="admin-categories-empty">Nessuna categoria. Clicca "Aggiungi" per iniziare.</p>}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="admin-root max-w-xl bg-white rounded-md max-h-[90vh] overflow-y-auto" data-testid="admin-categories-dialog">
          <DialogHeader><DialogTitle className="text-xl font-semibold">{form?.id ? "Modifica categoria" : "Nuova categoria"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid sm:grid-cols-2 gap-4">
              {!form.id && (
                <label className="block sm:col-span-2">
                  <span className="text-xs text-mute">Categoria</span>
                  <select className="admin-input mt-1" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} data-testid="field-event_type">
                    {available.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <span className="text-[11px] text-mute">Verranno create anche la pagina pubblica e la voce nel menu "Servizi".</span>
                </label>
              )}
              <label className="block"><span className="text-xs text-mute">Nome visualizzato</span>
                <input className="admin-input mt-1" value={form.title} placeholder="es. Matrimoni" onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="field-title" /></label>
              <label className="block"><span className="text-xs text-mute">Ordine in home page</span>
                <input className="admin-input mt-1" type="number" min="0" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="field-order" /></label>
              <label className="block sm:col-span-2"><span className="text-xs text-mute">Breve descrizione (mostrata accanto al nome in home)</span>
                <input className="admin-input mt-1" value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} data-testid="field-intro" /></label>
              <label className="block sm:col-span-2"><span className="text-xs text-mute">Fotografia della categoria (anteprima in home page)</span>
                <div className="mt-1 flex items-center gap-3">
                  {form.cover && <img src={imgUrl(form.cover)} alt="" className="w-20 h-24 object-cover" />}
                  <ImageUploader multiple={false} label={form.cover ? "Sostituisci" : "Carica fotografia"} onUploaded={(u) => setForm({ ...form, cover: u[0] })} testid="field-cover-upload" />
                </div></label>
              <label className="block sm:col-span-2 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} data-testid="field-visible" />
                Visibile in home page, nel menu e sul sito
              </label>
              <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                <button onClick={() => setForm(null)} className="btn-line !py-2.5 !px-5" data-testid="admin-categories-cancel-btn">Annulla</button>
                <button onClick={save} className="btn-ink !py-2.5 !px-5" data-testid="admin-categories-save-btn">Salva</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
